import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, stageWorldAt } from '../shaders/safeZone.glsl.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { genererCanauxRuban, MATCAP_GAIN, CHROME_BOX } from './lib/matcap.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_PRESETS } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Les anneaux ont leur propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'gyro',
  // le brief dit 'objects' ; la famille de l'orchestrateur est 'object'
  family: 'object',
  danceability: 0.86,
  energyLevel: 0.82,
  bpmMin: 115,
  bpmMax: 160,
  movement: 0.80,
  tonality: 0.45,
  enabled: true
};

// GYRO : cinq anneaux néon imbriqués comme un gyroscope (une sphère
// armillaire), autour du centre de composition. Sur les temps, un anneau
// BASCULE d'un quart de tour, d'un coup sec, et les autres continuent de
// tourner ; sur les drops, tous s'alignent dans un seul plan puis éclatent.
// Techno, trance, progressive, électro. Règles R1 à R19, preset party de
// vdjLook (R9 : danceability 0,86).
//
// LE CARDAN (JS, testable sans GPU : gimbalFrames) : l'anneau k est monté
// sur l'anneau k - 1 par un axe qui est un diamètre de k - 1 (X pour k
// impair, Y pour k pair ; l'anneau 0 tourne autour de la verticale) :
//   M0 = Ry(t0), M1 = M0 Rx(t1), M2 = M1 Ry(t2), M3 = M2 Rx(t3), M4 = M3 Ry(t4)
// Une bascule de l'anneau extérieur fait donc pivoter toute la structure
// intérieure. Tous les angles multiples de pi : les cinq anneaux sont dans
// le plan de l'écran, face à la caméra (l'alignement du drop).
//
// LES ANGLES : rotation continue (vitesses en rapports entiers du tempo,
// intégrées : moduler la vitesse, jamais la distance) + les bascules : un
// quart de tour en un huitième de temps, avec un léger dépassement et un
// retour (1 - exp(-8,5u) cos(9u)).
//
// LE RENDU : chaque anneau est un tore plat (section rectangulaire arrondie,
// normales exactes du profil). Faces en laque noire (matcap chrome assombri,
// R5) ; les quatre arêtes sont des lignes de néon tracées par leur DISTANCE
// EN PIXELS (R1 : la distance le long du profil, divisée par sa dérivée à
// l'écran) : coeur blanc 2 px, gaine 6 px de la couleur, halo exp(-d/10) x
// 0,35 ; graduations de cadran sur les faces, qui tournent avec l'anneau ;
// pivots chromés aux points d'axe ; au centre un noyau de lumière (6 % de
// la hauteur) qui pulse sur les kicks (derrière la pochette quand elle est
// au centre : le masque safeZone l'atténue, R19). MSAA 4x dans une cible
// privée. La bascule fait flasher l'anneau, qui laisse une traînée dans un
// FeedbackBuffer en demi-résolution (demi-vie d'un demi-temps, R15) ; tier 0
// sans traînées.
//
// LA TAILLE (R6) : le brief demande un grand anneau de 85 % de la hauteur.
// Une structure qui tourne dans tous les sens occupe sa sphère englobante ;
// projetée (perspective), elle doit rester sous la zone du nom de la station
// (y < 0,8 au centre) : le grand anneau prend la plus grande valeur qui tient
// (voir ringRadii, environ 73 % de la hauteur), le petit garde ses 30 %, les
// autres régulièrement entre les deux.

const TIER = { 0: { trails: false, seg: 112 }, 1: { trails: true, seg: 144 }, 2: { trails: true, seg: 192 } };
const CAM_D = 3.6;
const RINGS = 5;
// diamètres voulus (fractions de la hauteur d'écran) : 85 % -> 30 % ; en
// unités de hauteur (l'écran fait 2), le RAYON vaut donc cette fraction
const DIAM = [0.85, 0.7125, 0.575, 0.4375, 0.30];
const HW = 0.032, HT = 0.016, CR = 0.010;        // demi-largeur radiale, demi-épaisseur, rayon des arêtes (unités de hauteur, à l'échelle 1)
const PIVOT_R = 0.022;
const CORE_R = 0.06;                              // la sphère de lumière : 6 % de la hauteur (diamètre)
const FLIP_BEATS = 1 / 8;                         // la bascule : un huitième de temps
const NO_BPM = 128;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const ALIGN_BEATS = 1, HOLD_BEATS = 0.5, BURST_BEATS = 1;
const ZONE_TOP = 0.80, ZONE_PAD = 0.03;
// les vitesses continues (tours par mesure, rapports entiers), une table par phrase
export const SPEEDS = [[1 / 8, -1 / 4, 1 / 2, -1 / 4, 1 / 8], [-1 / 4, 1 / 8, -1 / 4, 1 / 2, -1 / 2], [1 / 4, 1 / 4, -1 / 8, -1 / 2, 1 / 4], [-1 / 8, 1 / 2, 1 / 4, -1 / 8, -1 / 4]];
const NEON = [0xff2a6d, 0x05d9e8, 0xfcee0c, 0x9d4edd, 0x39ff14, 0xff8c00, 0x00f5d4, 0xff3cf0];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
export function rng(seed) { let s = (seed >>> 0) || 1; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }
// la bascule : 0 -> 1 en un huitième de temps, dépassement de 8 % au plus, retour
export function flipEase(tBeats) {
  if (tBeats <= 0) return 0;
  const u = tBeats / FLIP_BEATS * 0.17;
  return 1 - Math.exp(-8.5 * u) * Math.cos(9 * u);
}
const easeInOut = (u) => { const x = clamp01(u); return x * x * (3 - 2 * x); };

// ------------------------------------------------------------- le cardan (JS)
export const AXES = ['y', 'x', 'y', 'x', 'y'];   // l'axe de chaque anneau dans le repère du précédent
export function gimbalFrames(angles, out) {
  const M = out || Array.from({ length: RINGS }, () => new THREE.Matrix4());
  const R = new THREE.Matrix4();
  for (let k = 0; k < RINGS; k++) {
    if (AXES[k] === 'y') R.makeRotationY(angles[k]); else R.makeRotationX(angles[k]);
    if (k === 0) M[0].copy(R); else M[k].multiplyMatrices(M[k - 1], R);
  }
  return M;
}

// la taille : la plus grande échelle s (rayons = DIAM / 2 x s) telle que la
// sphère englobante, projetée, reste sous la zone du nom de la station et
// dans le cadre (marge 5 %)
export function fitScale() {
  return ringRadii()[0] / DIAM[0];
}
// les rayons (unités de hauteur) : le grand anneau au plus grand qui tient,
// le petit à 30 % comme demandé, les trois autres régulièrement entre les deux
export function ringRadii() {
  const lim = Math.min(ZONE_TOP - ZONE_PAD, 1 - 0.10);
  // rayon projeté d'une sphère de rayon rho à la distance D : D rho / sqrt(D² - rho²)
  const rho = lim * CAM_D / Math.sqrt(CAM_D * CAM_D + lim * lim);
  const big = Math.min(DIAM[0], rho - HW - CR);
  const small = DIAM[RINGS - 1];
  return Array.from({ length: RINGS }, (_, k) => big + (small - big) * k / (RINGS - 1));
}

// ------------------------------------------------------------- la géométrie
// le tore plat : le profil (rectangle arrondi dans le plan (r, z)) tourné
// autour de Z. Attribut aEdge : la distance le long du profil au sommet
// d'arête le plus proche (0 au milieu de chaque arrondi).
function flatTorus(R, seg) {
  const prof = [];   // [r, z, nr, nz, s]
  const arc = (cx, cz, a0) => { for (let i = 0; i <= 4; i++) { const a = a0 + i / 4 * Math.PI / 2; prof.push([cx + Math.cos(a) * CR, cz + Math.sin(a) * CR, Math.cos(a), Math.sin(a)]); } };
  const ri = R - HW + CR, ro = R + HW - CR, zb = -HT + CR, zt = HT - CR;
  arc(ro, zb, -Math.PI / 2);         // coin extérieur bas
  arc(ro, zt, 0);                    // extérieur haut
  arc(ri, zt, Math.PI / 2);          // intérieur haut
  arc(ri, zb, Math.PI);              // intérieur bas
  prof.push(prof[0].slice());        // fermé
  // longueur le long du profil, et distance au sommet d'arête le plus proche
  const s = [0]; for (let i = 1; i < prof.length; i++) s.push(s[i - 1] + Math.hypot(prof[i][0] - prof[i - 1][0], prof[i][1] - prof[i - 1][1]));
  const apex = [2, 7, 12, 17].map((i) => s[i]), L = s[s.length - 1];
  const edgeD = s.map((v) => Math.min(...apex.map((a) => Math.min(Math.abs(v - a), L - Math.abs(v - a)))));
  const P = prof.length, pos = [], nrm = [], ed = [], idx = [];
  for (let j = 0; j <= seg; j++) {
    const a = j / seg * Math.PI * 2, c = Math.cos(a), sn = Math.sin(a);
    for (let i = 0; i < P; i++) {
      const [r, z, nr, nz] = prof[i];
      pos.push(r * c, r * sn, z); nrm.push(nr * c, nr * sn, nz); ed.push(edgeD[i]);
    }
  }
  for (let j = 0; j < seg; j++) for (let i = 0; i < P - 1; i++) {
    const a = j * P + i, b = a + 1, cc = a + P, d = cc + 1;
    idx.push(a, cc, b, b, cc, d);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('aEdge', new THREE.Float32BufferAttribute(ed, 1));
  g.setIndex(idx);
  return g;
}

const SAFE = `
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAtt(vec2 ndc) {
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(0.32, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;
const RING_VERTEX = `
  attribute float aEdge;
  varying vec3 vN;
  varying vec3 vLocal;
  varying float vEdge;
  varying vec2 vNdc;
  varying float vFace;
  void main() {
    vN = normalize(normalMatrix * normal);
    vLocal = position; vEdge = aEdge; vFace = normal.z;
    vec4 c = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    vNdc = c.xy / c.w;
    gl_Position = c;
  }
`;
const RING_FRAGMENT = `
  precision highp float;
  uniform sampler2D uMat;
  uniform vec3 uColor;
  uniform float uFlash;
  uniform float uTrail;            // 1 : la passe de traînée (le néon seul, si l'anneau flashe)
  uniform float uRadius;
  uniform float uGain;
  varying vec3 vN;
  varying vec3 vLocal;
  varying float vEdge;
  varying vec2 vNdc;
  varying float vFace;
  ${SAFE}
  void main() {
    vec3 n = normalize(vN);
    // R1 : la distance à l'arête en pixels (le long du profil / sa dérivée à l'écran)
    float px = vEdge / max(fwidth(vEdge), 1e-6);
    // (R1 compte les LARGEURS : coeur 2 px, gaine 6 px, soit 1 et 3 px de chaque côté de l'arête)
    float core = 1.0 - smoothstep(0.5, 1.3, px);
    float sheath = 1.0 - smoothstep(2.6, 3.6, px);
    float halo = exp(-px / 10.0) * 0.35 * (1.0 - smoothstep(30.0, 60.0, px));
    vec3 neon = uColor * halo;
    neon = mix(neon, uColor, sheath);
    neon = mix(neon, vec3(1.0, 0.97, 0.94) * 1.15, core);
    // les graduations : un trait tous les 5°, un long tous les 30°, sur les faces plates
    float phi = atan(vLocal.y, vLocal.x);
    float st = 6.2831853 / 72.0;
    float dTick = abs(phi - st * floor(phi / st + 0.5)) * uRadius;
    float tw = max(fwidth(phi * uRadius), 1e-6);
    float major = abs(phi - st * 6.0 * floor(phi / (st * 6.0) + 0.5)) * uRadius < tw * 1.5 ? 1.0 : 0.0;
    float rr = length(vLocal.xy) - uRadius;          // la position radiale sur la face
    float band = 1.0 - smoothstep(${(HW * 0.35).toFixed(4)}, ${(HW * 0.55).toFixed(4)}, abs(rr - ${(HW * 0.15).toFixed(4)}) * (1.0 - 0.4 * major));
    float tick = (1.0 - smoothstep(0.5, 1.5, dTick / tw)) * band * smoothstep(0.85, 0.98, abs(vFace));
    // la traînée (demi-résolution) : une lueur douce de la couleur autour des
    // arêtes, ni coeur blanc ni graduations - injectés tels quels, ils
    // crénelaient en tirets et voilaient l'anneau de rose
    if (uTrail > 0.5) { gl_FragColor = vec4(uColor * exp(-px / 1.5) * 0.45 * uFlash * safeAtt(vNdc), 1.0); return; }
    // la laque noire : le chrome du studio, très assombri ; les reflets restent nets
    vec4 m = texture2D(uMat, n.xy * 0.49 + 0.5);
    float body = m.b * m.b * ${MATCAP_GAIN.toFixed(2)};
    vec3 lac = vec3(0.010, 0.010, 0.014) + vec3(0.85, 0.88, 1.0) * (body * 0.06 + m.a * ${(CHROME_BOX * 0.55).toFixed(2)}) + uColor * 0.04 * body;
    vec3 col = lac + uColor * tick * 0.45;
    col = max(col, neon * (1.0 + 0.8 * uFlash));
    gl_FragColor = vec4(col * uGain * safeAtt(vNdc), 1.0);
  }
`;
const PIVOT_FRAGMENT = `
  precision highp float;
  uniform sampler2D uMat;
  uniform float uGain;
  varying vec3 vN;
  varying vec2 vNdc;
  ${SAFE}
  void main() {
    vec3 n = normalize(vN);
    vec4 m = texture2D(uMat, n.xy * 0.49 + 0.5);
    vec3 c = vec3(0.9, 0.92, 1.0) * (m.b * m.b * ${MATCAP_GAIN.toFixed(2)} * 0.7 + m.a * 0.8);
    gl_FragColor = vec4(c * uGain * safeAtt(vNdc), 1.0);
  }
`;
const PIVOT_VERTEX = `
  varying vec3 vN;
  varying vec2 vNdc;
  void main() {
    vN = normalize(normalMatrix * normal);
    vec4 c = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    vNdc = c.xy / c.w; gl_Position = c;
  }
`;
// le noyau : un disque de lumière face caméra (sphère de lumière), halo nul au bord du quad (R12)
const CORE_VERTEX = `
  uniform float uSize;
  varying vec2 vQ;
  varying vec2 vNdc;
  void main() {
    vec4 v = modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);
    v.xy += position.xy * uSize;
    vQ = position.xy;
    vec4 c = projectionMatrix * v;
    vNdc = c.xy / c.w; gl_Position = c;
  }
`;
const CORE_FRAGMENT = `
  precision highp float;
  uniform vec3 uColor;
  uniform float uPulse;
  uniform float uGain;
  varying vec2 vQ;
  varying vec2 vNdc;
  ${SAFE}
  void main() {
    float r = length(vQ) * 4.0;                  // r = 1 : le bord de la sphère (le quad fait 4 rayons)
    float ball = 1.0 - smoothstep(0.92, 1.0, r);
    vec3 c = mix(uColor, vec3(1.0), 0.55 * pow(max(0.0, 1.0 - r * r), 2.0)) * ball * (0.75 + 0.5 * uPulse);
    c += uColor * exp(-max(r - 1.0, 0.0) * 1.6) * 0.30 * (1.0 - smoothstep(2.2, 3.8, r)) * (1.0 - ball) * (0.6 + 0.8 * uPulse);
    gl_FragColor = vec4(c * uGain * safeAtt(vNdc), 1.0);
  }
`;
const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// R15 : décroissance indépendante de la cadence (demi-vie en temps), moins
// 1/255 tramé, pour que le tampon RGBA8 revienne au noir pur
const DECAY_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uPrev;
  uniform float uDecay;
  uniform float uSeed;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(uPrev, vUv).rgb * uDecay;
    float dither = fract(sin(dot(gl_FragCoord.xy + uSeed, vec2(12.9898, 78.233))) * 43758.5453);
    c = max(c - (1.0 + dither) / 255.0, 0.0);
    gl_FragColor = vec4(c, 1.0);
  }
`;
const DISPLAY_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  uniform sampler2D uTrailTex;
  uniform float uTrailOn;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb + texture2D(uTrailTex, vUv).rgb * uTrailOn, 1.0); }
`;

let MATCAP = null;
function matcapTexture() {
  if (MATCAP) return MATCAP;
  const m = genererCanauxRuban(256);
  const t = new THREE.DataTexture(m.data, m.size, m.size, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.NoColorSpace; t.minFilter = t.magFilter = THREE.LinearFilter; t.generateMipmaps = false; t.needsUpdate = true;
  MATCAP = t;
  return t;
}

// =============================================================================

export class GyroVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 2;
    this._L = TIER[this._tier];
    const f = parseInt(urlParam('gyroflip'), 10);
    this._forceFlip = Number.isFinite(f) && f >= 0 && f < RINGS ? f : null;
    this._forceAlign = urlParam('gyroalign') === '1';
    // bloom bas : vingt arêtes à coeur blanc, diffusées par le bloom partagé,
    // voilaient de gris tout l'intérieur des anneaux (25/255 à 0,18 ; R3)
    this._post = new VdjPost({ bloomStrength: 0.08, bloomThreshold: 0.86, grain: 0.0, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
    if (urlParam('gyroprobe') === '1' && typeof window !== 'undefined') window.__gyro = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color(0x000000);
    this._scale = fitScale(); this._radii = ringRadii();
    this._gain = { value: 1 };
    const safe = { uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._root = new THREE.Group();
    this._private = new THREE.Scene();
    this._private.add(this._root);
    // les anneaux (géométrie à l'échelle : les rayons ajustés, la section intacte)
    this._rings = [];
    for (let k = 0; k < RINGS; k++) {
      const R = this._radii[k];
      const U = Object.assign({ uMat: { value: matcapTexture() }, uColor: { value: new THREE.Vector3(1, 1, 1) }, uFlash: { value: 0 }, uTrail: { value: 0 }, uRadius: { value: R }, uGain: this._gain }, safe);
      const mesh = new THREE.Mesh(flatTorus(R, this._L.seg), new THREE.ShaderMaterial({ uniforms: U, vertexShader: RING_VERTEX, fragmentShader: RING_FRAGMENT, side: THREE.DoubleSide }));
      mesh.matrixAutoUpdate = false; mesh.frustumCulled = false;
      this._root.add(mesh);
      this._rings.push({ mesh, U, R });
    }
    // les pivots : deux par anneau intérieur, sur son axe, à mi-chemin de
    // l'anneau porteur (l'anneau 0 tourne sur un axe vertical virtuel : des
    // pivots dehors agrandissaient la sphère englobante d'environ 4 %)
    this._pivotMat = new THREE.ShaderMaterial({ uniforms: Object.assign({ uMat: { value: matcapTexture() }, uGain: this._gain }, safe), vertexShader: PIVOT_VERTEX, fragmentShader: PIVOT_FRAGMENT });
    this._pivotGeo = new THREE.SphereGeometry(PIVOT_R, 16, 12);
    this._pivots = [];
    for (let k = 1; k < RINGS; k++) for (const sg of [-1, 1]) {
      const m = new THREE.Mesh(this._pivotGeo, this._pivotMat); m.matrixAutoUpdate = false; m.frustumCulled = false;
      this._root.add(m); this._pivots.push({ m, k, sg });
    }
    // le noyau
    this._coreU = Object.assign({ uColor: { value: new THREE.Vector3(1, 1, 1) }, uPulse: { value: 0 }, uSize: { value: CORE_R / 2 * 4 }, uGain: this._gain }, safe);
    this._core = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.ShaderMaterial({ uniforms: this._coreU, vertexShader: CORE_VERTEX, fragmentShader: CORE_FRAGMENT, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
    this._core.frustumCulled = false; this._core.renderOrder = 10;
    this._root.add(this._core);

    // les traînées : FeedbackBuffer en demi-résolution
    this._fb = this._L.trails ? new FeedbackBuffer({ scale: 0.5 }) : null;
    this._decayU = { uPrev: { value: null }, uDecay: { value: 1 }, uSeed: { value: 0 } };
    this._decayScene = new THREE.Scene();
    this._decayCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const dq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._decayU, vertexShader: QUAD_VERTEX, fragmentShader: DECAY_FRAGMENT, depthTest: false, depthWrite: false }));
    dq.frustumCulled = false; this._decayScene.add(dq);
    this._trailScene = new THREE.Scene();

    // l'affichage : MSAA privé + traînées, un quad
    this._msaaTarget = null;
    this._dispU = { uTex: { value: null }, uTrailTex: { value: null }, uTrailOn: { value: 0 } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: QUAD_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer, sc, cam) { self._renderPrivate(renderer, cam); };
    scene.add(this._display);

    // l'état
    this._r = rng(31337);
    this._time = 0; this._clock = 0; this._clockPrev = 0; this._targetOffset = null; this._dtLast = 1 / 60;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM; this._buildup = 0;
    this._base = [0, 0, 0, 0, 0];       // l'angle continu + les bascules terminées
    this._flips = [];                   // { ring, start, amount }
    this._angles = [0, 0, 0, 0, 0];
    this._phrase = 0; this._colIdx = 0; this._turn = 0;
    this._flash = [0, 0, 0, 0, 0]; this._screenFlash = 0; this._pulse = 0;
    this._drop = null; this._lastMode = null;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._accent = null; this._paletteKey = null;
    this._frames = gimbalFrames(this._angles);
    this._stats = { flips: 0, drops: 0 };
    this._applyColours();
    this._pose();
  }

  _applyPalette(pal) {
    const v = pal && (pal.vividColors || pal.dominantColors);
    const key = v && v.length ? String(v[0]) : '';
    if (key === this._paletteKey) return;
    this._paletteKey = key; this._accent = v && v.length ? v[0] : null;
    this._applyColours();
  }
  // PHRASE : couleurs suivantes ; l'accent de la pochette sur l'anneau du milieu
  _applyColours() {
    for (let k = 0; k < RINGS; k++) {
      const hex = k === 2 && this._accent != null ? this._accent : NEON[(this._colIdx + k * 2) % NEON.length];
      const c = vdjColor(hex, new THREE.Color(), 0.92);
      this._rings[k].U.uColor.value.set(c.r, c.g, c.b);
    }
    const c = vdjColor(NEON[(this._colIdx + 3) % NEON.length], new THREE.Color(), 0.9);
    this._coreU.uColor.value.set(c.r, c.g, c.b);
  }

  // une bascule : un quart de tour (signe donné) de l'anneau k
  flip(k, sign) {
    this._flips.push({ ring: k, start: this._clock, amount: sign * Math.PI / 2 });
    this._flash[k] = 1;
    this._stats.flips++;
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._drops.sort((a, b) => a - b); this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0; while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) { if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true; this._dropIdx++; }
    return fired;
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._applyPalette(profile.artworkPalette);
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) { this._pose(); return; }   // pause : tout gèle, les traînées aussi
    this._time += dt; this._dtLast = dt;

    // l'horloge, en temps (Domino, Twist, Pipes)
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? THREE.MathUtils.clamp(bpm, 60, 200) : NO_BPM;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > 2) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else { const rate = this._bpm / 60; this._clock += Math.max(dt * rate * 0.5, dt * rate + err * Math.min(1, dt / 0.5)); }
    } else { this._clock += dt * this._bpm / 60; this._targetOffset = null; }
    const clock = this._clock, dBeat = Math.max(0, clock - this._clockPrev);
    const beat = Math.floor(clock) !== Math.floor(this._clockPrev);
    const eighth = Math.floor(clock * 2) !== Math.floor(this._clockPrev * 2);
    const bar = Math.floor(clock / 4) !== Math.floor(this._clockPrev / 4);
    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else phrase = Math.floor(clock / 16) !== Math.floor(this._clockPrev / 16);

    const director = profile.director;
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._buildup += (build - this._buildup) * (1 - Math.exp(-dt / 0.8));
    const bands = director && director.hasReliableGrid && director.bands ? director.bands : null;

    // DROP : alignement en un temps, flash, éclatement
    let drop = hasGrid && this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop && !this._drop) this._startAlign(clock);

    // la rotation continue (intégrée : la vitesse est modulée, jamais la distance)
    const sp = SPEEDS[this._phrase % SPEEDS.length], speedUp = 1 + 1.5 * this._buildup;
    if (!this._drop && !this._forceAlign) for (let k = 0; k < RINGS; k++) this._base[k] += sp[k] * Math.PI * 2 * dBeat / 4 * speedUp;

    // TEMPS (croche en montée) : une bascule ; MESURE : deux, en sens opposés
    const flipNow = this._buildup > 0.5 ? eighth : beat;
    if (flipNow && !this._drop && !this._forceAlign) {
      if (bar) {
        const a = this._r() < 0.5 ? 0 : 1, b2 = 2 + Math.floor(this._r() * 3);
        this.flip(a, 1); this.flip(b2, -1);
      } else {
        let k;
        if (this._forceFlip != null) k = this._forceFlip;
        else if (bands) {
          // graves -> grands anneaux, aigus -> petits
          const w = clamp01(bands.high / Math.max(1e-3, bands.low + bands.high));
          k = Math.min(RINGS - 1, Math.floor(w * RINGS + (this._r() - 0.5)));
          k = Math.max(0, k);
        } else k = this._turn++ % RINGS;
        this.flip(k, this._r() < 0.5 ? 1 : -1);
      }
    }
    if (beat) this._pulse = 1;
    // PHRASE : couleurs et vitesses suivantes
    if (phrase && !this._drop) { this._phrase++; this._colIdx++; this._applyColours(); }

    // les bascules : ajoutées aux angles ; repliées dans la base une fois posées
    const ang = this._base.slice();
    this._flips = this._flips.filter((f) => {
      const t = clock - f.start;
      if (t > 6 * FLIP_BEATS * 8) { this._base[f.ring] += f.amount; ang[f.ring] += f.amount; return false; }
      ang[f.ring] += f.amount * flipEase(t);
      return true;
    });
    // l'alignement : vers le multiple de pi le plus proche, en un temps
    if (this._drop) {
      const t = clock - this._drop.start;
      if (t < ALIGN_BEATS) {
        const u = easeInOut(t / ALIGN_BEATS);
        for (let k = 0; k < RINGS; k++) ang[k] = this._drop.from[k] + (this._drop.to[k] - this._drop.from[k]) * u;
      } else if (t < ALIGN_BEATS + HOLD_BEATS) {
        for (let k = 0; k < RINGS; k++) ang[k] = this._drop.to[k];
        if (!this._drop.flashed) { this._drop.flashed = true; if (this._comfort.requestFlash(this._time)) this._screenFlash = 1; for (let k = 0; k < RINGS; k++) this._flash[k] = 1; }
      } else {
        // l'éclatement : chacun sur son axe, un demi-tour ou trois quarts, élastique
        for (let k = 0; k < RINGS; k++) this._base[k] = this._drop.to[k];
        for (let k = 0; k < RINGS; k++) this._flips.push({ ring: k, start: clock, amount: (k % 2 ? -1 : 1) * Math.PI * (k % 3 === 0 ? 1.5 : 1) });
        this._drop = null;
      }
    }
    if (this._forceAlign) for (let k = 0; k < RINGS; k++) ang[k] = Math.round(this._base[k] / Math.PI) * Math.PI;
    this._angles = ang;

    const decay = Math.exp(-dt / 0.12);
    for (let k = 0; k < RINGS; k++) this._flash[k] *= Math.exp(-dt / 0.18);
    this._screenFlash *= decay;
    this._pulse *= Math.exp(-dt / 0.15);
    const est = 0.04 + 0.2 * this._screenFlash;
    this._gain.value = this._comfort.measure(est, dt) * (1 + 0.5 * this._screenFlash);
    this._post.update(vdjBeatChroma(clock - Math.floor(clock), 60 / this._bpm));
    this._pose();
  }

  _startAlign(clock) {
    // les angles du moment (bascules en cours comprises), vers le multiple de pi le plus proche
    const from = this._angles.slice();
    for (let k = 0; k < RINGS; k++) this._base[k] = from[k];
    this._flips = [];
    this._drop = { start: clock, from, to: from.map((a) => Math.round(a / Math.PI) * Math.PI), flashed: false };
    this._stats.drops++;
  }

  // la pose : les cadres du cardan, au centre de composition (R19)
  _pose() {
    const cam = this._camera;
    cam.fov = THREE.MathUtils.radToDeg(2 * Math.atan(1 / CAM_D));
    cam.near = 0.05; cam.far = 40;
    cam.position.set(0, 0, CAM_D); cam.rotation.set(0, 0, 0); cam.up.set(0, 1, 0);
    cam.updateProjectionMatrix(); cam.updateMatrixWorld();
    const centre = stageWorldAt(cam, CAM_D, _centre);
    const F = gimbalFrames(this._angles, this._frames);
    const T = new THREE.Matrix4().makeTranslation(centre.x, centre.y, centre.z);
    for (let k = 0; k < RINGS; k++) {
      const ring = this._rings[k];
      ring.mesh.matrix.multiplyMatrices(T, F[k]);
      ring.mesh.matrixWorldNeedsUpdate = true;
      ring.U.uFlash.value = this._flash[k];
    }
    // les pivots de l'anneau k : sur son axe (dans le repère de l'anneau
    // porteur), entre les deux anneaux ; ceux de l'anneau 0 sur la verticale, dehors
    for (const p of this._pivots) {
      const k = p.k, carrier = F[k - 1];
      const r = (this._rings[k].R + HW + this._rings[k - 1].R - HW) / 2;
      const axis = AXES[k] === 'y' ? [0, r * p.sg, 0] : [r * p.sg, 0, 0];
      p.m.matrix.multiplyMatrices(T, carrier).multiply(new THREE.Matrix4().makeTranslation(axis[0], axis[1], axis[2]));
      p.m.matrixWorldNeedsUpdate = true;
    }
    this._core.position.copy(centre); this._core.updateMatrix();
    this._coreU.uPulse.value = this._pulse;
  }

  _renderPrivate(renderer, camera) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(_size); w = b.x; h = b.y; }
    if (!this._msaaTarget || this._msaaTarget.width !== w || this._msaaTarget.height !== h) {
      if (this._msaaTarget) this._msaaTarget.dispose();
      this._msaaTarget = new THREE.WebGLRenderTarget(w, h, { samples: 4, depthBuffer: true, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    }
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    renderer.setClearColor(0x000000, 1);
    // les traînées : décroissance (demi-vie d'un demi-temps), puis les anneaux qui flashent
    if (this._fb && !this._paused) {
      this._fb.ensureSize(renderer);
      const halfLife = 0.5 * 60 / this._bpm;
      this._decayU.uPrev.value = this._fb.read.texture;
      this._decayU.uDecay.value = Math.pow(0.5, this._dtLast / halfLife);
      this._decayU.uSeed.value = (this._decayU.uSeed.value + 17.3) % 1000;
      renderer.setRenderTarget(this._fb.write);
      renderer.render(this._decayScene, this._decayCam);
      // (l'injection : les anneaux en mode traînée, additifs, seulement s'ils flashent)
      const flashing = this._rings.filter((r) => r.U.uFlash.value > 0.02);
      if (flashing.length) {
        const prevAuto = renderer.autoClear; renderer.autoClear = false;
        for (const r of this._rings) { r.U.uTrail.value = 1; r.mesh.material.blending = THREE.AdditiveBlending; r.mesh.material.transparent = true; r.mesh.material.depthWrite = false; r.mesh.visible = r.U.uFlash.value > 0.02; }
        for (const p of this._pivots) p.m.visible = false;
        this._core.visible = false;
        renderer.render(this._private, camera);
        for (const r of this._rings) { r.U.uTrail.value = 0; r.mesh.material.blending = THREE.NormalBlending; r.mesh.material.transparent = false; r.mesh.material.depthWrite = true; r.mesh.visible = true; }
        for (const p of this._pivots) p.m.visible = true;
        this._core.visible = true;
        renderer.autoClear = prevAuto;
      }
      this._fb.swap();
      this._dispU.uTrailTex.value = this._fb.read.texture;
      this._dispU.uTrailOn.value = 1;
    }
    renderer.setRenderTarget(this._msaaTarget);
    renderer.clear(true, true, false);
    renderer.render(this._private, camera);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    this._dispU.uTex.value = this._msaaTarget.texture;
    if (!this._fb) { this._dispU.uTrailTex.value = this._msaaTarget.texture; this._dispU.uTrailOn.value = 0; }
    else if (this._paused && this._fb.read) { this._dispU.uTrailTex.value = this._fb.read.texture; this._dispU.uTrailOn.value = 1; }
  }

  getProbe() {
    return { tier: this._tier, clock: +this._clock.toFixed(3), angles: this._angles.map((a) => +a.toFixed(4)), flips: this._flips.length, drop: !!this._drop, scale: +this._scale.toFixed(4), phrase: this._phrase, stats: Object.assign({}, this._stats) };
  }

  dispose() {
    if (this._scene) {
      if (this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
      this._scene.background = this._prevBackground || null;
    }
    for (const r of this._rings || []) { r.mesh.geometry.dispose(); r.mesh.material.dispose(); }
    if (this._pivotGeo) this._pivotGeo.dispose();
    if (this._pivotMat) this._pivotMat.dispose();
    for (const m of [this._core, this._display]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    if (this._decayScene) this._decayScene.traverse((o) => { if (o.isMesh) { o.geometry.dispose(); o.material.dispose(); } });
    if (this._fb) this._fb.dispose();
    if (this._msaaTarget) this._msaaTarget.dispose();
    if (typeof window !== 'undefined' && window.__gyro === this) window.__gyro = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
const _centre = new THREE.Vector3();
